import { useEffect, useState } from "react";
import { Box, Stack, Typography, Autocomplete, TextField, Divider, Button, Chip, Skeleton } from "@mui/material";
import { useResponsive } from "../../utils/mobileresponsive";
import AgentOverview from "./components/AgentOverview";
import { SectionHeader } from "../Dashboard/components/SectionHeader";
import PersonIcon from "@mui/icons-material/Person";
import ProductionCard from "./components/ProductionCard";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import Overrides from "./components/OverridesCard";
import LeadershipMilestones from "./components/LeadershipCard";
import ClientSummary from "./components/ClientSummaryCard";
import PerformanceTrend from "./components/PerformanceTrend";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import GroupsIcon from "@mui/icons-material/Groups";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import ConfirmationModal from "../../components/confirmation-modal";
import { generateButtonSx } from "../../utils/elementor-style-override";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import useFetchAgentDetails from "./hooks/useFetchAgentDetails";
import useFetchAgentsOptions from "../../sharedhooks/Agents/useFetchAllAgents";
import useUpdateAgentStatus from "./hooks/useUpdateAgentStatus";
import SnackbarNotification from "../../components/ui/SnackBarNotification";
import { isFieldAgent } from "../../utils/roleDetection";

const AgentDetails = () => {
	const stored = localStorage.getItem("loggedin_data");
	const user = stored ? JSON.parse(stored) : null;
	const roles = Array.isArray(user?.roles) ? user.roles : [];
	const isAgent = isFieldAgent(roles);
	const { isMobile } = useResponsive();
	const { agents, loading: agentOptionsLoading } = useFetchAgentsOptions();
	const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
	const [currentAgent, setCurrentAgent] = useState(null);

	useEffect(() => {
		if (agents?.length > 0 && !currentAgent) {
			setCurrentAgent(agents[0]);
		}
	}, [agents, currentAgent]);

	const selectedAgentId = isAgent ? undefined : currentAgent?.ID;

	const { details: agentDetails, loading: detailsLoading, refetch } = useFetchAgentDetails(selectedAgentId);
	const { updateStatus, loading: updateLoading } = useUpdateAgentStatus();

	const isActivated = Number(agentDetails?.details?.is_activated) === 1;
	// Matches AgentOverview below, so the header and the card settle together.
	const statusLoading = detailsLoading || agentOptionsLoading;

	const [confirmation, setConfirmation] = useState({
		open: false,
		action: null,
		row: null,
	});

	const handleAgentStatusToggle = () => {
		setConfirmation({
			open: true,
			action: isActivated ? "deactivate" : "reactivate",
			row: currentAgent,
		});
	};

	const handleCloseConfirmation = () => {
		setConfirmation({
			open: false,
			action: null,
			row: null,
		});
	};

	const handleConfirmAction = async () => {
		try {
			const newStatus = confirmation.action === "deactivate" ? 0 : 1;
			const data = {
				agent_id: selectedAgentId,
				status_to: newStatus,
			};
			const result = await updateStatus(data);

			if (!result?.success) {
				setSnackbar({ open: true, message: "Failed to update agent status. Please try again.", severity: "error" });
				return;
			}

			setSnackbar({ open: true, message: "Agent status updated successfully.", severity: "success" });
			handleCloseConfirmation();
			refetch();
		} catch (error) {
			console.error(error);
			setSnackbar({ open: true, message: "Unexpected error while updating agent status.", severity: "error" });
		}
	};

	return (
		<Box p={4}>
			{!isAgent && (
				<>
					<Stack direction={{ xs: "column", lg: "row" }} alignItems="center !important" justifyContent={"space-between"} spacing={2} px={3}>
						<Typography variant={isMobile ? "h2" : "h4"} sx={{ fontWeight: "bold !important", color: "#1D54A2", letterSpacing: 4 }}>
							AGENT DETAILS
						</Typography>
						<Stack
							direction={{ xs: "column", sm: "row" }}
							alignItems={{ xs: "flex-start", sm: "center" }}
							spacing={{ xs: 0.75, sm: 2 }}
							width={{ xs: "100%", lg: "auto" }}
						>
							<Typography fontWeight={600} sx={{ fontSize: { xs: 13, sm: 14 }, whiteSpace: "nowrap" }}>
								Current Agent:
							</Typography>
							<Autocomplete
								size="small"
								options={agents || []}
								loading={agentOptionsLoading}
								value={currentAgent}
								onChange={(e, newValue) => setCurrentAgent(newValue)}
								getOptionLabel={(option) => option?.display_name || ""}
								isOptionEqualToValue={(option, value) => option?.ID === value?.ID}
								sx={{
									width: { xs: "100%", sm: 220, md: 250 },
									"& .MuiButtonBase-root": {
										backgroundColor: "transparent",
										"&:hover": { color: "#1D54A2" },
									},
								}}
								slotProps={{
									popper: {
										sx: {
											"& .MuiAutocomplete-paper": { width: "inherit !important" },
										},
									},
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										placeholder="Select agent"
										sx={{
											"& .MuiInputBase-input": {
												padding: { xs: "6px 10px", sm: "8.5px 14px" },
												fontSize: { xs: 13, sm: 14 },
											},
											"& fieldset": { borderColor: "#4E4E4E !important" },
											"&:hover fieldset": { borderColor: "#4E4E4E !important" },
											"&.Mui-focused fieldset": { borderColor: "#4E4E4E !important" },
											"& .MuiAutocomplete-input": { border: "none !important" },
										}}
									/>
								)}
							/>
						</Stack>
					</Stack>
					<Box my={3}>
						<Divider />
					</Box>
				</>
			)}

			<Box mb={4}>
				<SectionHeader
					icon={PersonIcon}
					title={"Agent Overview"}
					badge={
						statusLoading ? (
							<Skeleton variant="rounded" width={150} height={24} sx={{ borderRadius: 99 }} />
						) : (
							<Chip
								label={isActivated ? "Opted In for Service" : "Opted Out for Service"}
								size="small"
								sx={{
									backgroundColor: isActivated ? "success.verylight" : "error.verylight",
									color: isActivated ? "success.dark" : "error.dark",
									fontWeight: 600,
									fontSize: "0.75rem",
								}}
							/>
						)
					}
					right={
						!isAgent &&
						(statusLoading ? (
							<Skeleton variant="rounded" width={165} height={31} sx={{ borderRadius: 1 }} />
						) : (
							<Button
								size="small"
								variant="contained"
								startIcon={isActivated ? <PersonOffIcon /> : <RestartAltIcon />}
								onClick={handleAgentStatusToggle}
								sx={{
									fontWeight: 700,
									px: 2,
									gap: 0.5,
									color: isActivated ? "#DC2626 !important" : "#16A34A !important",
									backgroundColor: isActivated ? "#FEF2F2 !important" : "#ECFDF5 !important",
									...generateButtonSx(
										isActivated ? { bg: "#FEF2F2 !important", text: "#DC2626" } : { bg: "#ECFDF5 !important", text: "#16A34A" },
										isActivated ? { bg: "#FEE2E2 !important", text: "#DC2626" } : { bg: "#D1FAE5 !important", text: "#16A34A" }
									),
								}}
							>
								{isActivated ? "Deactivate Agent" : "Activate Agent"}
							</Button>
						))
					}
				/>
				<AgentOverview agent={agentDetails?.details} loading={detailsLoading || agentOptionsLoading} />
			</Box>

			<Stack direction={{ xs: "column", md: "row" }} gap={2} mb={6}>
				<Box flex={1} width={"100%"}>
					<SectionHeader icon={PersonAddIcon} title={"Personal Production"} />
					<ProductionCard data={agentDetails?.personal_production} color="#185FA5" loading={detailsLoading || agentOptionsLoading} />
				</Box>
				<Box flex={1} width={"100%"}>
					<SectionHeader icon={AccountTreeIcon} title={"Network Production"} />
					<ProductionCard data={agentDetails?.network_production} color="#1D9E75" loading={detailsLoading || agentOptionsLoading} />
				</Box>
			</Stack>

			<Stack direction={{ xs: "column", md: "row" }} gap={2} mb={4}>
				<Box flex={1} width={"100%"}>
					<SectionHeader icon={CurrencyExchangeIcon} title={"Overrides"} />
					<Overrides data={agentDetails?.overrides} loading={detailsLoading || agentOptionsLoading} />
				</Box>
				<Box flex={1} width={"100%"}>
					<SectionHeader icon={EmojiEventsIcon} title={"Leadership Milestones"} />
					<LeadershipMilestones data={agentDetails?.leadership} loading={detailsLoading || agentOptionsLoading} />
				</Box>
				<Box flex={1} width={"100%"}>
					<SectionHeader icon={GroupsIcon} title={"Client Summary"} />
					<ClientSummary
						totalActiveMembers={agentDetails?.client_summary?.total_active_members}
						totalPolicyCount={agentDetails?.client_summary?.total_policy_count}
						totalApplicantCount={agentDetails?.client_summary?.total_applicant_count}
						loading={detailsLoading || agentOptionsLoading}
					/>
				</Box>
			</Stack>

			<Box width={"100%"} mb={4}>
				<SectionHeader icon={ShowChartIcon} title={"Agent Performance Trend"} />
				<PerformanceTrend statuses={agentDetails?.performance_trend} loading={detailsLoading || agentOptionsLoading} />
			</Box>
			<ConfirmationModal
				open={confirmation.open}
				onClose={handleCloseConfirmation}
				onConfirm={handleConfirmAction}
				isLoading={updateLoading}
				title={confirmation.action === "deactivate" ? "Deactivate Agent?" : "Activate Agent?"}
				description={
					confirmation.action === "deactivate"
						? `${confirmation.row?.display_name}'s access to the portal will be revoked, and they will be excluded from all commission computations — their own earnings drop to $0 and their book stops counting toward their upline's override. Downlines keep their levels and their own computations. Closed cutoffs are not affected.`
						: `${confirmation.row?.display_name}'s access to the portal will be restored and they will be included in commission computations again. The current cutoff is recomputed from their book of business.`
				}
				confirmText={confirmation.action === "deactivate" ? "Deactivate Agent" : "Activate Agent"}
				confirmColor={confirmation.action === "deactivate" ? "#DC2626 !important" : "#16A34A !important"}
			/>

			<SnackbarNotification
				open={snackbar.open}
				message={snackbar.message}
				severity={snackbar.severity}
				onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
			/>
		</Box>
	);
};
export default AgentDetails;
